
import { useState } from 'react';

interface Service {
  id: number;
  title: string;
  description: string;
  icon: string;
  bgGradient: string;
  color: string;
  features: string[];
}

export default function ServicesSection() {
  const [expandedCard, setExpandedCard] = useState<number | null>(null);

  const services: Service[] = [
    {
      id: 1,
      title: 'إدارة حسابات المشاهير',
      description: 'خدمات متكاملة لإدارة حسابات المشاهير والشخصيات العامة بأعلى معايير الجودة',
      icon: 'ri-star-line',
      bgGradient: 'from-yellow-400 to-yellow-500',
      color: 'yellow',
      features: ['إنشاء المحتوى', 'الجداول الزمنية للنشر', 'رفع التفاعل', 'إدارة الحملات']
    },
    {
      id: 2,
      title: 'إدارة حسابات الأفراد',
      description: 'حلول مخصصة للأفراد الراغبين في بناء حضور رقمي قوي ومؤثر',
      icon: 'ri-user-star-line',
      bgGradient: 'from-blue-600 to-blue-700',
      color: 'blue',
      features: ['بناء الهوية الرقمية', 'استراتيجيات النمو', 'تحليل الأداء', 'استشارات مخصصة']
    },
    {
      id: 3,
      title: 'إدارة حسابات الشركات',
      description: 'استراتيجيات تسويقية متقدمة لتعزيز حضور الشركات على منصات التواصل',
      icon: 'ri-building-line',
      bgGradient: 'from-yellow-400 to-yellow-500',
      color: 'yellow',
      features: ['التسويق الرقمي', 'إدارة السمعة', 'حملات إعلانية', 'تقارير شاملة']
    },
    {
      id: 4,
      title: 'تحليل البيانات والإحصائيات',
      description: 'تقارير تفصيلية وتحليلات دقيقة لقياس الأداء وتحسين النتائج',
      icon: 'ri-line-chart-line',
      bgGradient: 'from-blue-600 to-blue-700',
      color: 'blue',
      features: ['تحليل التفاعل', 'تتبع النمو', 'تقارير دورية', 'توصيات مخصصة']
    },
    {
      id: 5,
      title: 'إنتاج المحتوى الإبداعي',
      description: 'محتوى احترافي ومبتكر يجذب الجمهور ويعزز التفاعل',
      icon: 'ri-palette-line',
      bgGradient: 'from-yellow-400 to-yellow-500',
      color: 'yellow',
      features: ['تصميم جرافيك', 'إنتاج فيديو', 'كتابة محتوى', 'تصوير احترافي']
    },
    {
      id: 6,
      title: 'إدارة الحملات الإعلانية',
      description: 'حملات إعلانية مستهدفة لتحقيق أقصى عائد على الاستثمار',
      icon: 'ri-megaphone-line',
      bgGradient: 'from-blue-600 to-blue-700',
      color: 'blue',
      features: ['استهداف دقيق', 'تحسين الميزانية', 'اختبار A/B', 'تقارير الأداء']
    }
  ];

  const toggleCard = (id: number) => {
    setExpandedCard(expandedCard === id ? null : id);
  };

  return (
    <section id="services" className="py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-20">
          <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Cairo, sans-serif' }}>
            خدماتنا
          </h2>
          <div className="w-24 h-1.5 bg-gradient-to-r from-yellow-400 via-yellow-500 to-blue-600 mx-auto rounded-full"></div>
          <p className="text-xl text-gray-600 mt-6 max-w-2xl mx-auto" style={{ fontFamily: 'Cairo, sans-serif' }}>
            نقدم مجموعة شاملة من الخدمات الاحترافية لإدارة حساباتك على منصات التواصل الاجتماعي
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <div
              key={service.id}
              className="group relative bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 cursor-pointer"
              onClick={() => toggleCard(service.id)}
              style={{
                animation: `slideUpFade 0.6s ease-out ${index * 0.2}s both`
              }}
            >
              {/* Gradient Background on Hover */}
              <div className={`absolute inset-0 bg-gradient-to-br ${service.bgGradient} ${expandedCard === service.id ? 'opacity-100' : 'opacity-0'} transition-opacity duration-500`}></div>

              {/* Content */}
              <div className="relative z-10 p-8">
                {/* Icon */}
                <div className={`w-20 h-20 flex items-center justify-center mb-6 rounded-2xl bg-gradient-to-br ${service.bgGradient} ${expandedCard === service.id ? 'bg-white' : ''} transition-all duration-500 shadow-lg`}>
                  <i className={`${service.icon} text-4xl ${expandedCard === service.id ? `text-${service.color}-500` : 'text-white'} transition-colors duration-500`}></i>
                </div>

                {/* Title */}
                <h3 className={`text-2xl font-bold ${expandedCard === service.id ? 'text-white' : 'text-gray-900'} mb-4 transition-colors duration-500`} style={{ fontFamily: 'Cairo, sans-serif' }}>
                  {service.title}
                </h3>

                {/* Expandable Content */}
                <div className={`overflow-hidden transition-all duration-500 ${expandedCard === service.id ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                  {/* Description */}
                  <p className="text-white/90 mb-6 leading-relaxed" style={{ fontFamily: 'Cairo, sans-serif' }}>
                    {service.description}
                  </p>

                  {/* Features */}
                  <div className="space-y-3 mb-6">
                    {service.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-3">
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                        <span className="text-white text-sm" style={{ fontFamily: 'Cairo, sans-serif' }}>
                          {feature}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* CTA Button */}
                  <a
                    href="https://wa.me/966501234567"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-white text-gray-900 rounded-full hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap font-semibold"
                    style={{ fontFamily: 'Cairo, sans-serif' }}
                  >
                    <i className="ri-whatsapp-line text-xl"></i>
                    <span>تواصل الآن</span>
                  </a>
                </div>

                {/* Click Indicator */}
                {expandedCard !== service.id && (
                  <div className="mt-4 text-center">
                    <i className="ri-arrow-down-s-line text-2xl text-gray-400 animate-bounce"></i>
                  </div>
                )}
              </div>

              {/* Decorative Border */}
              <div className={`absolute inset-0 border-2 ${expandedCard === service.id ? `border-${service.color}-500` : 'border-transparent'} rounded-3xl transition-all duration-500`}></div>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes slideUpFade {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </section>
  );
}
